import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * Spec 032 T051 follow-up — "Needs your input" view.
 *
 * Lists applications that bailed in CompletionGate review because of
 * unresolved fields. The user types one answer per question; the server
 * stores each as a `ApplicationCustomAnswer` row keyed on the question
 * label so the next form with the same question (case-insensitive
 * substring match) resolves it automatically.
 */

const unresolvedFieldSchema = z.object({
  label: z.string(),
});

export const needsInputApplicationSchema = z.object({
  id: z.string(),
  jobTitle: z.string(),
  company: z.string(),
  jobUrl: z.string().nullable(),
  platform: z.string(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
  unresolvedFields: z.array(unresolvedFieldSchema),
  noAgreementConsent: z.boolean(),
});

export const needsInputListResponseSchema = z.object({
  items: z.array(needsInputApplicationSchema),
});

export type NeedsInputApplication = z.infer<typeof needsInputApplicationSchema>;
export type NeedsInputListResponse = z.infer<typeof needsInputListResponseSchema>;

// — POST /needs-input/:applicationId/answers ——————————————————————————
//
// Body: list of { label, answer } pairs. Each becomes a CustomAnswer row
// with `questionPattern = label` and `answer = answer`. The 100-row
// per-user cap from US6 still applies — over the cap the call returns 422.

export const resolveQuestionsSchema = z.object({
  answers: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(500),
        answer: z.string().trim().min(1).max(2000),
      }),
    )
    .min(1)
    .max(50),
  /**
   * When true, after persisting the answers re-enqueue the application
   * so the auto-applier takes another shot at it with the freshly-saved
   * CustomAnswer rows. Defaults to false — the existing "Save answers"
   * flow stays unchanged for users who only want to update their
   * profile without re-trying the failed application.
   */
  retry: z.boolean().optional().default(false),
});

export class ResolveQuestionsDto extends createZodDto(resolveQuestionsSchema) {}

/** Mirrors the service result for the `POST /needs-input/:applicationId/answers` route. */
export type ResolveQuestionsResponse = {
  created: number;
  skipped: number;
  retried: boolean;
  /** Exactly which labels reached the bank, so the client never clears one that did not. */
  savedLabels: string[];
};
